import { useMemo } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import BlankImage from '../../../../assets/images/blank-image.png';
import { Button } from '../../../../components/common/buttons/Button';
import { usePostUserSellTractorInquiries } from '../../../../hooks/api/inquiry/mutations/usePostUserSellTractorInquiries';
import { useVirtualNumberInquiry } from '../../../../hooks/product/useVirtualNumberInquiry';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { IOpenMarketProduct } from '../../../../interfaces/OpenMarketProduct.interfaces';
import { getOpenMarketProductTitle } from '../../../../utils/getter/getOpenMarketProductTitle.util';
import { getOpenMarketDetailPageURL } from '../../../../utils/getter/getProductDetailPageURL.util';
import { getOpenMarketProductPrice } from '../../../../utils/getter/getProductPrice.util';
import { ProductStatusBanner } from '../components/ProductStatusBanner';
import { ProductStatusText } from '../components/ProductStatusText';

export type OpenMarketProductPreviewCardPageType = 'home' | 'odp' | 'ndp';

interface OpenMarketProductPreviewCardProps {
  product: IOpenMarketProduct;
  pageType: OpenMarketProductPreviewCardPageType;
  position?: 'top' | 'bottom';
  onClick?: () => void;
  isInquiryButtonVisible?: boolean;
}

// TODO : Card 공통 컴포넌트 분리 https://gint-team.atlassian.net/browse/PVM-1963

export const OpenMarketProductPreviewCard = ({
  product,
  pageType,
  position,
  onClick,
  isInquiryButtonVisible = false,
}: OpenMarketProductPreviewCardProps) => {
  const navigate = useNavigate();
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const { mutate: postUserSellTractorInquiries } = usePostUserSellTractorInquiries();

  const { callWithVirtualNumber } = useVirtualNumberInquiry({
    productId: product?.id || '',
    productCode: product?.code || '',
    sellerPhoneNumber: product?.user?.phoneNumber || '',
  });

  const productDetailPageURL = useMemo<string>(() => {
    return getOpenMarketDetailPageURL(product.id, product.machine.machineType);
  }, [product.id, product.machine.machineType]);

  const price = useMemo<string>(() => {
    return getOpenMarketProductPrice(product.status, product.hopeSellingPrice);
  }, [product]);

  const title = useMemo<string>(() => {
    return getOpenMarketProductTitle({
      brandName: product.machine.brand.name,
      machine: product.machine,
    });
  }, [product]);

  const handleInquiryButtonClick = () => {
    if (!product) {
      return;
    }

    ampli.마켓오픈마켓매물전화상담버튼클릭({
      '게시글 제목':
        getOpenMarketProductTitle({ brandName: product.machine?.brand?.name, machine: product.machine }) ?? '',
      '매물 코드': product?.code,
    });

    postUserSellTractorInquiries({ reqBody: { userSellTractorId: product.id } });

    ampli.pvmProductInquiry({
      title: getOpenMarketProductTitle({ brandName: product.machine?.brand?.name, machine: product.machine }) ?? '',
      type: 'MemberToMember',
      page_type: 'home',
      ps: product.machine.engineOutputInPs,
      manufacturedYear: product.manufacturedYear,
      operationTime: product.operationTime,
    });
    ReactGA.event('pvm_product_inquiry', {
      category: 'pvm',
      action: 'pvm_product_inquiry',
      value: 'MemberToMember',
    });

    // 안심번호로 전화 걸기
    callWithVirtualNumber();
  };

  return (
    <Container
      onClick={(e) => {
        e.stopPropagation();
        ampli.pvmClickProductCardInSwiper({
          title,
          id: product.id,
          page_type: pageType,
          category: product.machine.machineType === 'combine' ? 'OPEN-MARKET-COMBINE' : 'OPEN-MARKET-TRACTOR',
          position: position ?? 'none',
        });
        onClick?.();
        navigate(productDetailPageURL);
      }}
    >
      <ImageWrap>
        {product.status === 'SOLDOUT' && <ProductStatusText text={'판매 완료'} />}

        <Image
          src={
            product.images.filter((image) => image.type === 'LIST' || image.type === 'LEFT').length > 0
              ? product.images.filter((image) => image.type === 'LIST' || image.type === 'LEFT')[0].imageUrl
              : BlankImage
          }
          alt={
            product.images.filter((image) => image.type === 'LIST' || image.type === 'LEFT').length > 0
              ? product.images.filter((image) => image.type === 'LIST' || image.type === 'LEFT')[0].imageUrl
              : BlankImage
          }
          isBlurred={product.status === 'SOLDOUT'}
          loading={'lazy'}
        />

        {product.status === 'SOLDOUT' && <ProductStatusBanner productStatus={product.status} />}
      </ImageWrap>

      <ProductInfo isBlurred={product.status === 'SOLDOUT'}>
        <Title>{title}</Title>

        <YearAndOprationTime>{`${String(product.manufacturedYear).slice(2, 4)}년식 · ${Number(
          product.operationTime,
        ).toLocaleString()}시간`}</YearAndOprationTime>

        <PriceWrap>
          <Price>{price}</Price>
        </PriceWrap>
      </ProductInfo>
      {isInquiryButtonVisible && (
        <ButtonWrapper>
          <Button
            borderType={'square'}
            color={'outlinedGreyLight'}
            onClick={handleInquiryButtonClick}
            size={'sm'}
            text={{ content: '문의하기', size: 'md' }}
            icon={{ fileName: 'AngleRight', position: 'right', size: 'Medium', fill: '#505363' }}
          />
        </ButtonWrapper>
      )}
    </Container>
  );
};

const Container = styled.div`
  min-width: 100%;
  overflow: visible;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  gap: 8px;

  z-index: 1;

  &:hover {
    cursor: pointer;
  }
`;

const ImageWrap = styled.div`
  width: 100%;

  padding-top: 128px;

  position: relative;

  border-radius: 8px;

  background-color: #f2f3f5;
`;

const Image = styled.img<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  left: 0;
  top: 0;

  object-fit: cover;

  position: absolute;

  border-radius: 8px;

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};
`;

const ProductInfo = styled.div<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};

  gap: var(--radius-small, 4px);
`;

const YearAndOprationTime = styled.h2`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  /* Display/Small/s-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 15.6px */

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  align-self: stretch;

  overflow: hidden;
  text-overflow: ellipsis;
`;

const Title = styled.h1`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 20.8px */

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  align-self: stretch;

  overflow: hidden;
  text-overflow: ellipsis;
`;

const PriceWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  white-space: nowrap;

  gap: 4px;
`;

const Price = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  flex-shrink: 0;
  white-space: nowrap;

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const DiscountWrap = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const Discount = styled.span`
  color: var(--Light-Scheme-Color-primary-red, #ff3049);

  /* Display/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 15.6px */
`;
const ButtonWrapper = styled.div`
  width: 83px;

  display: flex;
  justify-content: flex-start;
  align-items: flex-start;

  border-radius: var(--radius-medium, 8px);
`;
